// ============================================================================
// Finance Inbox — dashboard widget for finance / executive / superuser roles.
//
// Surfaces a small list of finance-actionable counts as clickable rows. Each
// row is a deep link into the relevant pre-filtered list view so Jose can
// see "12 ready to invoice" and tap straight through to the Project Work
// page filtered to that stage.
//
// Visibility is gated by the parent dashboard, not here — the widget itself
// doesn't decide who should see it. That keeps role logic in one place.
//
// Graceful degradation: each row checks its data for null and renders an
// em-dash if the underlying table isn't available. That way the absence of
// a downstream module (Contracts, for example) doesn't break the widget.
// ============================================================================

import { Link } from 'react-router-dom';
import { ArrowUpRight, AlertTriangle, FileText, Receipt } from 'lucide-react';
import { WidgetCard, WidgetSkeleton } from '../widget-card';
import { useFinanceInbox, type FinanceInboxCounts } from './use-inbox-counts';

interface Props {
  enabled: boolean;
  myEid: string | null;
}

interface Row {
  key: keyof FinanceInboxCounts;
  label: string;
  to: string;
  icon: typeof FileText;
  // Optional hint shown when the count is null (e.g. table doesn't exist yet).
  unavailableHint?: string;
}

const ROWS: Row[] = [
  {
    key: 'ready_to_invoice',
    label: 'Completed projects awaiting invoicing',
    // /project-work doesn't currently parse a stage filter param, but
    // landing the user on the module lets them see the Completed bucket
    // immediately. When the module gains URL filtering this still works.
    to: '/project-work?stage=Completed',
    icon: Receipt,
  },
  {
    key: 'open_finance_tasks',
    label: 'Open finance tasks (assigned to me)',
    to: '/tasks?category=Finance&assignee=me',
    icon: FileText,
  },
  {
    key: 'subs_no_valid_coi',
    label: 'Working subs with no valid COI',
    // The card IS the doorway to the list. Same predicate, same count.
    to: '/people/subcontractors?tab=insurance',
    icon: AlertTriangle,
  },
];

export function FinanceWidget({ enabled, myEid }: Props) {
  const { data, isLoading, error } = useFinanceInbox(enabled, myEid);

  if (!enabled) return null;

  return (
    <WidgetCard
      title="Finance inbox"
      subtitle="What's stacked up for Finance."
      minHeight={240}
    >
      {error && (
        <p className="text-xs text-st-danger-fg">Couldn't load: {error}</p>
      )}
      {!error && isLoading && <WidgetSkeleton lines={4} />}
      {!error && !isLoading && data && (
        <ul className="-mx-2 divide-y divide-tcs-border/60">
          {ROWS.map((row) => {
            const value = data[row.key];
            const Icon = row.icon;
            const display = value === null ? '—' : value.toLocaleString('en-US');
            return (
              <li key={row.key}>
                <Link
                  to={row.to}
                  className="group flex items-center justify-between gap-3 px-2 py-2 text-sm text-tcs-text hover:bg-tcs-surface"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <Icon
                      size={14}
                      className="shrink-0 text-st-ok-fg"
                      aria-hidden
                    />
                    <span className="truncate">{row.label}</span>
                  </span>
                  <span className="flex items-center gap-1 text-tcs-text-muted">
                    <span className="tabular-nums font-medium text-tcs-text">
                      {display}
                    </span>
                    <ArrowUpRight
                      size={12}
                      className="text-tcs-text-faint group-hover:text-tcs-text-secondary"
                      aria-hidden
                    />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </WidgetCard>
  );
}
